import { DOCUMENT } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, inject, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import type { SpriConfig } from '@spri/services/config';
import { SpriConfigService } from '@spri/services/config';
import { SpriMediaWatcherService } from '@spri/services/media-watcher';
import { SpriPlatformService } from '@spri/services/platform';
import { SPRI_VERSION } from '@spri/version';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { PatientFormLayoutComponent } from './layouts/patient-form-layout/patient-form-layout.component';
import { FuturisticLayoutComponent } from './layouts/vertical/futuristic/futuristic.component';
import { SpriLayoutComponent } from './layouts/vertical/spri/spri.component';
import { ThinLayoutComponent } from './layouts/vertical/thin/thin.component';

export enum LayoutOption {
  Empty = 'empty',
  Centered = 'centered',
  Enterprise = 'enterprise',
  Material = 'material',
  Modern = 'modern',
  Classic = 'classic',
  Classy = 'classy',
  Compact = 'compact',
  Futuristic = 'futuristic',
  Thin = 'thin',
  Dense = 'dense',

  PatientForm = 'patient-form-layout',
}

@Component({
  selector: 'spri-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    EmptyLayoutComponent,
    PatientFormLayoutComponent,
    FuturisticLayoutComponent,
    SpriLayoutComponent,
    ThinLayoutComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  config: SpriConfig;
  layout: string;
  scheme: 'dark' | 'light';
  theme: string;
  private unsubscribeAll = new Subject<void>();

  /**
   * Constructor
   */
  private activatedRoute = inject(ActivatedRoute);
  private document = inject(DOCUMENT) as Document;
  private renderer2 = inject(Renderer2);
  private router = inject(Router);
  private spriConfigService = inject(SpriConfigService);
  private spriMediaWatcherService = inject(SpriMediaWatcherService);
  private spriPlatformService = inject(SpriPlatformService);

  /**
   * On init
   */
  ngOnInit(): void {
    // Set the theme and scheme based on the configuration
    combineLatest([
      this.spriConfigService.config$,
      this.spriMediaWatcherService.onMediaQueryChange$([
        '(prefers-color-scheme: dark)',
        '(prefers-color-scheme: light)',
      ]),
    ])
      .pipe(
        takeUntil(this.unsubscribeAll),
        map(([config, mql]) => {
          const options = {
            scheme: config.scheme,
            theme: config.theme,
          };

          // If the scheme is set to 'auto'...
          if (config.scheme === 'auto') {
            // Decide the scheme using the media query
            options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
          }

          return options;
        })
      )
      .subscribe((options) => {
        // Store the options
        this.scheme = options.scheme;
        this.theme = options.theme;

        // Update the scheme and theme
        this._updateScheme();
        this._updateTheme();
      });

    // Subscribe to config changes
    this.spriConfigService.config$.pipe(takeUntil(this.unsubscribeAll)).subscribe((config: SpriConfig) => {
      // Store the config
      this.config = config;

      // Update the layout
      this._updateLayout();
    });

    // Subscribe to NavigationEnd event
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribeAll)
      )
      .subscribe(() => {
        // Update the layout
        this._updateLayout();
      });

    // Set the app version
    this.renderer2.setAttribute(this.document.querySelector('[ng-version]'), 'app-version', SPRI_VERSION);

    // Set the OS name
    this.renderer2.addClass(this.document.body, this.spriPlatformService.osName);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the selected layout
   */
  private _updateLayout(): void {
    // Get the current activated route
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config.layout;

    // 2. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      // Check if there is a 'layout' data
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
        // Set the layout
        this.layout = path.routeConfig.data.layout;
      }
    });

    // 3. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    // Query parameters have the highest priority and override route data
    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }
  }

  /**
   * Update the selected scheme
   *
   * @private
   */
  private _updateScheme(): void {
    // Remove class names for all schemes
    this.document.body.classList.remove('light', 'dark');

    // Add class name for the currently selected scheme
    this.document.body.classList.add(this.scheme);
  }

  /**
   * Update the selected theme
   *
   * @private
   */
  private _updateTheme(): void {
    // Find the class name for the previously selected theme and remove it
    this.document.body.classList.forEach((className: string) => {
      if (className.startsWith('theme-')) {
        this.document.body.classList.remove(className, className.split('-')[1]);
      }
    });

    // Add class name for the currently selected theme
    this.document.body.classList.add(this.theme);
  }
}
